document.documentElement.classList.remove('no-js');

(() => {
  const root = document;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let facetTimer;

  const markBound = element => {
    if (!element || element.dataset.agBound === 'true') return false;
    element.dataset.agBound = 'true';
    return true;
  };

  const setLayer = (layer, open) => {
    if (!layer) return;
    layer.classList.toggle('is-open', open);
    layer.setAttribute('aria-hidden', String(!open));
    root.querySelector('[data-ag-overlay]')?.classList.toggle('is-open', open);
    document.body.classList.toggle('ag-lock', open);
  };

  const setFilters = (open, scope = root) => {
    const panel = scope.querySelector('[data-ag-filter-panel]') || root.querySelector('[data-ag-filter-panel]');
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open && window.matchMedia('(max-width: 900px)').matches));
    (scope.querySelector('.ag-filter-backdrop') || root.querySelector('.ag-filter-backdrop'))?.classList.toggle('is-open', open);
    document.body.classList.toggle('ag-lock', open);
  };

  const initSliders = (scope = root) => {
    scope.querySelectorAll('[data-ag-slider]').forEach(slider => {
      if (!markBound(slider)) return;
      const track = slider.querySelector('[data-ag-track]');
      slider.querySelector('[data-ag-next]')?.addEventListener('click', () => track?.scrollBy({ left: track.clientWidth * .8, behavior: 'smooth' }));
      slider.querySelector('[data-ag-prev]')?.addEventListener('click', () => track?.scrollBy({ left: -track.clientWidth * .8, behavior: 'smooth' }));
    });
  };

  const initHero = (scope = root) => {
    scope.querySelectorAll('[data-ag-hero]').forEach(hero => {
      if (!markBound(hero)) return;
      const slides = [...hero.querySelectorAll('[data-ag-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-ag-hero-dot]')];
      const interval = Number(hero.dataset.interval) || 3000;
      let active = 0;
      let timer;
      const show = index => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === active);
          slide.setAttribute('aria-hidden', String(i !== active));
        });
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (slides.length > 1 && !reduceMotion.matches) timer = window.setInterval(() => show(active + 1), interval);
      };
      hero.querySelector('[data-ag-hero-next]')?.addEventListener('click', () => { show(active + 1); start(); });
      hero.querySelector('[data-ag-hero-prev]')?.addEventListener('click', () => { show(active - 1); start(); });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', stop);
      hero.addEventListener('focusout', start);
      start();
    });
  };

  const initReviews = (scope = root) => {
    scope.querySelectorAll('[data-ag-reviews]').forEach(carousel => {
      if (!markBound(carousel)) return;
      const track = carousel.querySelector('[data-ag-reviews-track]');
      const interval = Number(carousel.dataset.interval) || 5500;
      let timer;
      const step = () => {
        const card = track?.querySelector('.ag-review-card');
        return card ? card.getBoundingClientRect().width + 18 : 320;
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (!reduceMotion.matches && track?.children.length > 1) {
          timer = window.setInterval(() => {
            const atEnd = Math.ceil(track.scrollLeft + track.clientWidth) >= track.scrollWidth - 4;
            track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + step(), behavior: 'smooth' });
          }, interval);
        }
      };
      carousel.querySelector('[data-ag-reviews-next]')?.addEventListener('click', () => { track?.scrollBy({ left: step(), behavior: 'smooth' }); start(); });
      carousel.querySelector('[data-ag-reviews-prev]')?.addEventListener('click', () => { track?.scrollBy({ left: -step(), behavior: 'smooth' }); start(); });
      carousel.addEventListener('mouseenter', stop);
      carousel.addEventListener('mouseleave', start);
      start();
    });
  };

  const updatePriceRange = wrapper => {
    const max = Number(wrapper.dataset.rangeMax) || 0;
    const minSlider = wrapper.querySelector('[data-ag-price-slider="min"]');
    const maxSlider = wrapper.querySelector('[data-ag-price-slider="max"]');
    const minNumber = wrapper.querySelector('[data-ag-price-number="min"]');
    const maxNumber = wrapper.querySelector('[data-ag-price-number="max"]');
    const progress = wrapper.querySelector('[data-ag-price-progress]');
    if (!minSlider || !maxSlider || !minNumber || !maxNumber || !progress || !max) return;
    const minValue = Math.max(0, Math.min(Number(minSlider.value), max));
    const maxValue = Math.max(minValue, Math.min(Number(maxSlider.value), max));
    minSlider.value = String(minValue);
    maxSlider.value = String(maxValue);
    minNumber.value = minValue === 0 ? '' : String(minValue);
    maxNumber.value = maxValue === max ? '' : String(maxValue);
    progress.style.left = ((minValue / max) * 100) + '%';
    progress.style.right = (100 - (maxValue / max) * 100) + '%';
  };

  const initPriceRanges = (scope = root) => {
    scope.querySelectorAll('[data-ag-price-range]').forEach(wrapper => {
      if (!markBound(wrapper)) return;
      const max = Number(wrapper.dataset.rangeMax) || 0;
      const minSlider = wrapper.querySelector('[data-ag-price-slider="min"]');
      const maxSlider = wrapper.querySelector('[data-ag-price-slider="max"]');
      const minNumber = wrapper.querySelector('[data-ag-price-number="min"]');
      const maxNumber = wrapper.querySelector('[data-ag-price-number="max"]');
      const syncFromSliders = event => {
        let min = Number(minSlider.value);
        let high = Number(maxSlider.value);
        if (min > high) {
          if (event.target === minSlider) high = min;
          else min = high;
        }
        minSlider.value = String(min);
        maxSlider.value = String(high);
        updatePriceRange(wrapper);
      };
      const syncFromNumbers = () => {
        minSlider.value = String(Math.max(0, Math.min(Number(minNumber.value || 0), max)));
        maxSlider.value = String(Math.max(0, Math.min(Number(maxNumber.value || max), max)));
        updatePriceRange(wrapper);
      };
      minSlider?.addEventListener('input', syncFromSliders);
      maxSlider?.addEventListener('input', syncFromSliders);
      minNumber?.addEventListener('input', syncFromNumbers);
      maxNumber?.addEventListener('input', syncFromNumbers);
      updatePriceRange(wrapper);
    });
  };

  const applySavedView = (scope = root) => {
    const view = localStorage.getItem('sidhm-product-view') || 'grid';
    scope.querySelectorAll('.ag-product-grid').forEach(grid => grid.classList.toggle('is-list', view === 'list'));
    scope.querySelectorAll('[data-ag-grid-view]').forEach(button => {
      const active = button.dataset.agGridView === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  };

  const initGallery = (scope = root) => {
    scope.querySelectorAll('[data-ag-gallery]').forEach(gallery => {
      if (!markBound(gallery)) return;
      const main = gallery.querySelector('[data-ag-gallery-main]');
      gallery.querySelectorAll('[data-ag-gallery-thumb]').forEach(thumb => thumb.addEventListener('click', () => {
        if (!main) return;
        main.src = thumb.dataset.src;
        main.alt = thumb.dataset.alt || main.alt;
        gallery.querySelectorAll('[data-ag-gallery-thumb]').forEach(item => item.classList.toggle('is-active', item === thumb));
      }));
    });
  };

  const initProduct = (scope = root) => {
    scope.querySelectorAll('.ag-product-form').forEach(form => {
      if (!markBound(form)) return;
      const quantity = form.querySelector('[data-ag-quantity]');
      form.querySelector('[data-ag-quantity-minus]')?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
      form.querySelector('[data-ag-quantity-plus]')?.addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; });
      form.querySelector('[data-ag-variant-select]')?.addEventListener('change', event => {
        const option = event.target.selectedOptions[0];
        const price = root.querySelector('[data-ag-product-price]');
        const compare = root.querySelector('[data-ag-compare-price]');
        const add = form.querySelector('[data-ag-add-to-cart]');
        if (price) price.textContent = option.dataset.price || '';
        if (compare) {
          compare.textContent = option.dataset.comparePrice || '';
          compare.hidden = !option.dataset.comparePrice;
        }
        if (add) {
          const available = option.dataset.available === 'true';
          add.disabled = !available;
          add.textContent = available ? 'Add to cart — ' + option.dataset.price : 'Sold out';
        }
      });
    });
  };

  const initRecommendations = (scope = root) => {
    scope.querySelectorAll('[data-ag-recommendations][data-loaded="false"]').forEach(section => {
      if (!markBound(section) || !section.dataset.url) return;
      fetch(section.dataset.url)
        .then(response => response.text())
        .then(html => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = parsed.querySelector('[data-ag-recommendations]');
          if (replacement) {
            section.replaceWith(replacement);
            init(replacement);
          }
        })
        .catch(() => {});
      });
  };

  const initReviewForms = (scope = root) => {
    scope.querySelectorAll('[data-ag-review-module]').forEach(module => {
      if (!markBound(module)) return;
      const panel = module.querySelector('[data-ag-review-panel]');
      const open = () => {
        if (!panel) return;
        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        panel.querySelector('input:not([type="hidden"])')?.focus();
      };
      const close = () => {
        if (!panel) return;
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      };
      module.querySelector('[data-ag-review-open]')?.addEventListener('click', open);
      module.querySelector('[data-ag-review-close]')?.addEventListener('click', close);
      if (module.querySelector('.ag-form-success') || module.querySelector('.errors')) open();
    });
  };

  const formUrl = form => {
    const url = new URL(form.action || window.location.href, window.location.origin);
    url.search = '';
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') url.searchParams.append(key, value);
    });
    return url.toString();
  };

  const renderFacets = async (url, pushState = true) => {
    const current = root.querySelector('[data-ag-faceted-view]');
    const previousScrollY = window.scrollY;
    if (!current) {
      window.location.href = url;
      return;
    }
    current.classList.add('is-loading');
    current.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Unable to update results');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-ag-faceted-view]');
      if (!replacement) throw new Error('Missing results');
      current.replaceWith(replacement);
      if (pushState) history.pushState({}, '', url);
      init(replacement);
      requestAnimationFrame(() => window.scrollTo({ top: previousScrollY, behavior: 'auto' }));
    } catch (error) {
      window.location.href = url;
    }
  };

  const queueFacetSubmit = form => {
    window.clearTimeout(facetTimer);
    facetTimer = window.setTimeout(() => renderFacets(formUrl(form)), 180);
  };

  const init = (scope = root) => {
    initSliders(scope);
    initHero(scope);
    initReviews(scope);
    initPriceRanges(scope);
    initGallery(scope);
    initProduct(scope);
    initRecommendations(scope);
    initReviewForms(scope);
    applySavedView(scope);
  };

  root.querySelector('[data-ag-mobile-open]')?.addEventListener('click', () => setLayer(root.querySelector('[data-ag-mobile-drawer]'), true));
  root.querySelector('[data-ag-mobile-close]')?.addEventListener('click', () => setLayer(root.querySelector('[data-ag-mobile-drawer]'), false));
  root.querySelector('[data-ag-search-open]')?.addEventListener('click', () => setLayer(root.querySelector('[data-ag-search-overlay]'), true));
  root.querySelector('[data-ag-search-close]')?.addEventListener('click', () => setLayer(root.querySelector('[data-ag-search-overlay]'), false));
  root.querySelector('[data-ag-overlay]')?.addEventListener('click', () => {
    setLayer(root.querySelector('[data-ag-mobile-drawer]'), false);
    setLayer(root.querySelector('[data-ag-search-overlay]'), false);
  });

  root.addEventListener('click', event => {
    const openFilters = event.target.closest('[data-ag-filter-open]');
    const closeFilters = event.target.closest('[data-ag-filter-close]');
    const viewButton = event.target.closest('[data-ag-grid-view]');
    const filterLink = event.target.closest('.ag-purpose-chip, .ag-filter-purpose-link, .ag-active-filters a, .ag-filter-clear, .ag-pagination a');
    const mobileAdd = event.target.closest('[data-ag-mobile-add]');
    if (openFilters) setFilters(true, openFilters.closest('[data-ag-faceted-view]'));
    if (closeFilters) setFilters(false, closeFilters.closest('[data-ag-faceted-view]') || root);
    if (viewButton) {
      const view = viewButton.dataset.agGridView;
      localStorage.setItem('sidhm-product-view', view);
      applySavedView(viewButton.closest('[data-ag-faceted-view]') || root);
    }
    if (filterLink && filterLink.closest('[data-ag-faceted-view]')) {
      event.preventDefault();
      renderFacets(filterLink.href);
    }
    if (mobileAdd) root.querySelector('.ag-product-form [data-ag-add-to-cart]')?.click();
  });

  root.addEventListener('change', event => {
    const form = event.target.closest('[data-ag-filter-form]');
    if (form && !event.target.matches('[data-ag-price-number]') && !window.matchMedia('(max-width: 900px)').matches) queueFacetSubmit(form);
    const sort = event.target.closest('.ag-sort');
    if (sort && event.target.matches('select') && sort.closest('[data-ag-faceted-view]')) {
      event.preventDefault();
      renderFacets(formUrl(sort));
    }
  });

  root.addEventListener('submit', event => {
    const form = event.target;
    if ((form.matches('[data-ag-filter-form]') || form.matches('.ag-sort')) && form.closest('[data-ag-faceted-view]')) {
      event.preventDefault();
      setFilters(false, form.closest('[data-ag-faceted-view]'));
      renderFacets(formUrl(form));
    }
  });

  window.addEventListener('popstate', () => {
    if (root.querySelector('[data-ag-faceted-view]')) renderFacets(window.location.href, false);
  });

  init();
})();


(function(){
  function showSlide(carousel,index){
    var slides=Array.prototype.slice.call(carousel.querySelectorAll('[data-ag-product-slide]'));
    if(!slides.length)return;
    var next=(index+slides.length)%slides.length;
    slides.forEach(function(slide,slideIndex){
      var active=slideIndex===next;
      slide.classList.toggle('is-active',active);
      slide.setAttribute('aria-hidden',active?'false':'true');
      slide.setAttribute('tabindex',active?'0':'-1');
    });
    carousel.setAttribute('data-current-slide',String(next));
  }
  function move(carousel,direction){
    var current=parseInt(carousel.getAttribute('data-current-slide')||'0',10);
    showSlide(carousel,current+direction);
  }
  document.addEventListener('click',function(event){
    var button=event.target.closest('[data-ag-carousel-action]');
    if(!button)return;
    var carousel=button.closest('[data-ag-card-carousel]');
    if(!carousel)return;
    event.preventDefault();
    event.stopPropagation();
    move(carousel,button.getAttribute('data-ag-carousel-action')==='next'?1:-1);
  });
  var starts=new WeakMap();
  document.addEventListener('touchstart',function(event){
    var carousel=event.target.closest('[data-ag-card-carousel]');
    if(!carousel||!event.changedTouches.length)return;
    starts.set(carousel,event.changedTouches[0].clientX);
  },{passive:true});
  document.addEventListener('touchend',function(event){
    var carousel=event.target.closest('[data-ag-card-carousel]');
    if(!carousel||!event.changedTouches.length||!starts.has(carousel))return;
    var distance=event.changedTouches[0].clientX-starts.get(carousel);
    starts.delete(carousel);
    if(Math.abs(distance)<42)return;
    move(carousel,distance<0?1:-1);
  },{passive:true});
})();
